import abbreviations from '~shared/consts/timezoneAbbreviations'

type TimezoneFormatOption = 'city' | 'long' | 'abbr' | 'offset'

/**
 * The timezone-name half of a formatted date: "Pacific Daylight Time" out of
 * "8/14/2026, Pacific Daylight Time".
 *
 * With a `timeZoneName` set, Intl puts the date first and the name second, so
 * the segment after the comma is the name. Falling back to '' rather than
 * asserting: a missing segment means the runtime's locale data disagreed with
 * that assumption, and an empty timezone label is survivable where a throw
 * mid-render is not.
 */
function timeZoneNamePart(date: Date, formatOpts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('en-US', formatOpts).format(date).split(', ')[1] ?? ''
}

/**
 * Formats an IANA timezone string into a specified representation.
 *
 * @param {string} timezone - The IANA timezone string, e.g., 'America/Los_Angeles'.
 * @param {TimezoneFormatOption | TimezoneFormatOption[]} format - One of 'long', 'abbr', 'offset', or an array of these
 *   values.
 * @param {Date} date - The reference date used to determine the 'long', 'abbr' and 'offset'
 *
 * @returns {string} The formatted timezone string.
 */
export function formatTimezone(
  timezone: string,
  format: TimezoneFormatOption | TimezoneFormatOption[] = 'long',
  date: Date = new Date(),
): string {
  if (!timezone) return ''

  const formatOpts: Intl.DateTimeFormatOptions = { timeZone: timezone }
  const formatArray: TimezoneFormatOption[] = Array.isArray(format) ? format : [format]

  for (const fmt of formatArray) {
    switch (fmt) {
      case 'city': {
        const parts = timezone.split('/')
        return `${parts[0]} / ${parts.pop()}`.replace(/_/g, ' ')
      }

      case 'long':
        formatOpts.timeZoneName = 'long'
        return timeZoneNamePart(date, formatOpts)

      case 'abbr': {
        formatOpts.timeZoneName = 'long'
        const long = timeZoneNamePart(date, formatOpts)
        const abbr = abbreviations[long] || abbreviations[long.replace('Standard ', '')]
        if (abbr) return abbr
        break
      }

      case 'offset': {
        let offset = ''
        try {
          formatOpts.timeZoneName = 'longOffset' // Note: Typescript parser may be outdated, 'longOffset' is valid
          offset = timeZoneNamePart(date, formatOpts)
        } catch {
          formatOpts.timeZoneName = 'short'
          offset = timeZoneNamePart(date, formatOpts)
        }
        return offset === 'GMT' ? 'GMT+00:00' : offset
      }

      default:
        throw new Error("Format option must be one of 'city', 'long', 'abbr' or 'offset'")
    }
  }

  return ''
}
